/**
 * PROPORTIONS DE TOUS LES ASSETS — source unique de vérité.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LA RÈGLE, en une phrase : **on déclare une taille dans le MONDE, jamais une
 * échelle de sprite.** Le moteur dérive l'échelle en divisant par la mesure.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Pourquoi ce fichier existe ───────────────────────────────────────────────
 *
 * Le pipeline de rendu normalise chaque modèle à son cadre : il ne conserve **aucune**
 * taille relative du monde réel. Deux objets aux dimensions incomparables sortent donc
 * du même calibre. Mesuré : une poubelle à 232 × 415 pixels, exactement la taille d'un
 * arbre (232 × 414) — à l'écran, une poubelle aussi haute qu'un chêne, et un banc large
 * comme la moitié d'une case.
 *
 * Régler cela sprite par sprite avec des `scale.set(0.72)` dispersés dans le moteur, c'est
 * garantir l'incohérence : chaque valeur est choisie à l'œil, dans son contexte, sans
 * référence commune — et la suivante se trompe autrement. C'est exactement ce qui s'était
 * produit.
 *
 * ── Comment on s'y prend ─────────────────────────────────────────────────────
 *
 * Deux nombres, et deux seulement, pour chaque asset :
 *
 * 1. **L'emprise voulue**, déclarée ici, en fraction de la largeur d'une case. C'est la
 *    seule décision de design du calcul, et elle est lisible : « un banc occupe un tiers
 *    de case » se discute, « scale = 0,571 » ne se discute pas.
 * 2. **L'emprise mesurée**, dans `assetMetrics.generated.ts`, produite par
 *    `npm run measure:assets`. Jamais écrite à la main (règle 15) : une table écrite se
 *    trompe et se désynchronise au premier re-rendu.
 *
 * L'échelle est le quotient. Un asset ajouté au pack apparaît tout seul dans la mesure ;
 * il ne lui manque qu'une ligne d'emprise voulue, et son absence se voit au chargement
 * plutôt qu'à l'écran.
 *
 * ── L'unité de référence ─────────────────────────────────────────────────────
 *
 * **Une case = 5 mètres.** Tout le reste s'exprime par rapport à elle. Ce n'est pas une
 * mesure du pack — c'est la convention qui rend les emprises ci-dessous discutables en
 * termes humains, et c'est ce qui permet de trancher « ce banc est-il à la bonne
 * taille ? » sans ouvrir un éditeur d'images.
 */

import { ASSET_OPAQUE_SIZE } from './assetMetrics.generated';
import { SYNTY_TILE } from './tileGeometry';

/** Côté d'une case, en mètres. Convention de référence, pas une mesure du pack. */
export const TILE_METRES = 5;

/** Emprise voulue, en mètres, convertie en fraction de case au moment du calcul. */
const METRES: Readonly<Record<string, number>> = {
  /* ── Mobilier urbain ────────────────────────────────────────────────────
     Ces objets bordent les rues et servent d'ÉTALON : c'est à eux que l'œil
     compare tout le reste. Une poubelle trop grosse fait paraître les
     bâtiments petits, pas l'inverse. */
  bin: 0.7,
  bench: 1.6,
  streetLight: 1.5,
  potplants: 1.3,

  /* ── Signalisation ──────────────────────────────────────────────────────
     Exception DOCUMENTÉE à la règle 19, tranchée par Benoit (18 août : « feux
     tricolores énormes »). À l'échelle du pack, le feu mesure 138 unités de
     haut — presque 7 m, un immeuble de deux étages : les modèles de
     signalisation de POLYGON MINI sont stylisés hors gabarit. Or le rapport
     qui compte à l'écran est signal ↔ rue (véhicules, lampadaires), pas
     signal ↔ pack. On les cale donc sur le lampadaire : le feu à peine plus
     large, le panneau nettement plus bas. */
  trafficLight: 1.05,
  stopSign: 0.8,

  /* ── Points de repère en gemmes (ROADMAP 2.3) ───────────────────────────
     Ils étaient au CALIBRE PAR DÉFAUT depuis leur livraison — invisible tant
     que `assetMetrics.generated.ts` n'était pas régénéré, puisque la table
     figée ne les contenait pas. C'est la règle 19 qui tranche leur taille :
     l'emprise déclarée est un plafond, l'échelle vient du PACK.

     Les valeurs sont celles mesurées à la boîte opaque (`measure:sprites`),
     converties en mètres : château d'eau 0,44 case = 2,2 m de large pour
     6,5 m de haut, piscine 1,14 case = 5,7 m, héliport 0,93 = 4,7 m, aire de
     jeux 0,77 = 3,9 m. On les laisse à leur taille naturelle : ce sont des
     repères, ils doivent se voir. */
  waterTower: 2.2,
  poolClub: 5.7,
  helipad: 4.7,
  playground: 3.9,

  /* ── Décorations achetables restées sans emprise ────────────────────────
     Révélées par la même régénération. Une jardinière et une fontaine sont du
     mobilier de place : on les cale entre le banc (1,6 m) et l'arbre (2,6 m),
     à l'échelle de ce qu'un passant contourne. */
  flowerbed: 1.8,
  fountain: 2.4,

  /* ── Nature ─────────────────────────────────────────────────────────────
     Ce sont des masses qui remplissent l'espace non bâti, pas des objets
     qu'on inspecte. Mais une case ne vaut que cinq mètres : un houppier de
     4 m la remplissait presque entièrement et la campagne devenait une forêt
     opaque — mesuré à 114 % de la largeur de case. 2,6 m laisse voir le sol
     entre deux troncs, ce qui est la raison d'être du décor. */
  tree1: 2.6,
  tree2: 2.6,
  tree3: 2.4,
  bush1: 1.5,
  rock1: 1.3,
  rock2: 1.4,

  /* ── Circulation ────────────────────────────────────────────────────────
     Une voiture fait 4,5 m de long, soit presque une case entière — mais elle
     est vue en diagonale sur une chaussée d'une case de large, donc son
     emprise APPARENTE tient dans la moitié. */
  'car-1': 2.4,
  'car-2': 2.4,
  'car-3': 2.4,
  'car-4': 2.4,
};

/**
 * Échelle d'affichage d'un asset, dérivée de sa mesure.
 *
 * Rend `null` si l'asset n'a pas d'emprise déclarée : l'appelant garde alors sa valeur
 * par défaut. Un `null` est préférable à une valeur inventée — il rend l'omission
 * visible au lieu de la maquiller.
 */
export function assetScale(name: string): number | null {
  const measured = ASSET_OPAQUE_SIZE[baseName(name)];
  const metres = METRES[baseName(name)];
  if (metres === undefined || measured === undefined) return null;

  return scaleFor(metres, measured[0]);
}

/** Rotations et variantes sont le même modèle vu autrement : même emprise. */
function baseName(name: string): string {
  return name.replace(/\.png$/, '').replace(/-(r[1-3]|v\d+|thumb)$/, '');
}

/** Échelle qui fait occuper `metres` mètres à un contenu large de `measuredPx` pixels. */
function scaleFor(metres: number, measuredPx: number): number {
  return (SYNTY_TILE.width * (metres / TILE_METRES)) / measuredPx;
}

/**
 * Échelle d'un BÂTIMENT : sa largeur à l'écran doit valoir son emprise de jeu.
 *
 * ── Le défaut que cette fonction corrige ─────────────────────────────────────
 *
 * Un bâtiment déclare une emprise en cases (`footprint`) : c'est le terrain qu'il occupe,
 * ce que le placement réserve, ce que le joueur paie. À l'écran, il devrait donc être
 * large d'autant de cases. **Il ne l'était pas, et pas de la même façon d'un modèle à
 * l'autre.** Mesuré sur les vingt sprites de base :
 *
 * | modèle           | emprise | largeur réelle | écart  |
 * | ---------------- | ------- | -------------- | ------ |
 * | `doctor-t1`      | 2 cases | 0,62 case      | × 3,20 |
 * | `workplace-t1`   | 2 cases | 0,66 case      | × 3,02 |
 * | `cinema-t3`      | 2 cases | 0,71 case      | × 2,84 |
 * | `grocery-t3`     | 2 cases | 1,60 case      | × 1,25 |
 * | `streetStand-t3` | 1 case  | 0,80 case      | × 1,25 |
 *
 * Le pipeline normalise chaque modèle à son cadre de rendu : il produit donc des images
 * de calibre comparable pour des objets de tailles incomparables. Conséquence à l'écran,
 * et c'est ce qu'on voit sur une ville avancée : **une échoppe 1 × 1 apparaît plus large
 * qu'un cabinet médical 2 × 2**. Il n'existe aucune échelle commune, donc aucune lecture
 * possible de la ville — les volumes ne racontent plus la progression qu'ils sont censés
 * raconter (D§6).
 *
 * ── Ce que corriger ici ne corrige PAS ───────────────────────────────────────
 *
 * Agrandir un sprite de ×3,2 à l'exécution rend les proportions justes et l'image molle :
 * on étire des pixels qui n'existent pas. La vraie correction est **dans le pipeline** —
 * cadrer la caméra de rendu sur `footprint × TILE_METRES` plutôt que sur la boîte
 * englobante du modèle, puis re-rendre. Cette fonction est la spécification exécutable de
 * ce que le pipeline devra produire : le jour où il le produira, elle rendra 1 partout.
 */
export function buildingScale(spriteName: string, footprint: number): number | null {
  const measured = ASSET_OPAQUE_SIZE[baseName(spriteName)];
  if (measured === undefined) return null;

  return scaleFor(footprint * TILE_METRES, measured[0]);
}

/**
 * Assets mesurés mais sans emprise déclarée.
 *
 * Exporté pour qu'un test puisse le vérifier : c'est ainsi qu'un décor ajouté au pack se
 * signale à la revue au lieu d'apparaître à l'écran au mauvais calibre. La liste des
 * bâtiments en est exclue — leur taille vient de leur emprise de jeu (`footprint`), pas
 * d'une déclaration de décor.
 */
export function assetsWithoutDeclaredSize(known: ReadonlySet<string>): readonly string[] {
  return Object.keys(ASSET_OPAQUE_SIZE)
    .filter((name) => !known.has(name) && METRES[name] === undefined)
    .sort();
}
